import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { Sparkles, Send, CheckCircle2, ShieldAlert, Wrench, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTION_CHIPS = [
  'Why is FBA Planned Units not fetching?',
  'RTO June data is missing.',
  'Blinkit projections look incorrect.',
  'Check Inventory Recon calculations.',
];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('radar_session_token') || ''}`,
  'Cache-Control': 'no-cache, no-store, must-revalidate',
});

/**
 * Floating "Ask AI" button + slide-over chat panel.
 *
 * Session-only conversation: state lives in this component; unmounting on tab
 * close or navigation-away clears it. No conversation is ever persisted to
 * localStorage or the backend.
 *
 * Apply Fix: shown only when the backend's /api/ai/permissions endpoint
 * confirms the caller is in the editor allowlist. Everyone else sees the
 * diagnosis and manual steps, no button.
 */
export default function AskAI() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role, content, meta? }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [canApplyFixes, setCanApplyFixes] = useState(false);
  const [applyingId, setApplyingId] = useState(null); // which message's fix is being applied
  const scrollRef = useRef(null);

  // Fetch permissions once — only needs to run when the user opens the panel.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/ai/permissions`, { headers: authHeaders() });
        if (!cancelled) setCanApplyFixes(!!res.data.can_apply_fixes);
      } catch {
        if (!cancelled) setCanApplyFixes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Auto-scroll to newest message on each update.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    setInput('');
    setSending(true);

    // Stable IDs so React keeps message state across re-renders / new messages.
    const userMsgId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const nextMessages = [...messages, { id: userMsgId, role: 'user', content: trimmed }];
    setMessages(nextMessages);

    try {
      // history is the transcript BEFORE this new user turn — the server
      // replays these to prime the model before answering the current message.
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const res = await axios.post(
        `${API}/ai/chat`,
        { message: trimmed, history },
        { headers: authHeaders(), timeout: 90000 },
      );
      const data = res.data;
      setMessages(m => [...m, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'assistant',
        content: data.answer || 'No response.',
        meta: {
          module: data.module,
          affected_sheet: data.affected_sheet,
          root_cause: data.root_cause,
          suggestion: data.suggestion,
        },
      }]);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setMessages(m => [...m, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'assistant',
        content: `⚠️ AI request failed: ${detail}`,
        meta: null,
      }]);
    } finally {
      setSending(false);
    }
  };

  const applyFix = async (msgId, suggestion) => {
    if (!suggestion || suggestion.action === 'explain_only') return;
    setApplyingId(msgId);
    try {
      const res = await axios.post(
        `${API}/ai/apply-fix`,
        { action: suggestion.action, params: suggestion.params },
        { headers: authHeaders() },
      );
      const result = res.data.result || {};
      const applied = result.changed;
      toast.success(applied ? 'Fix applied — reloading soon' : (result.reason || 'No change needed'));
      setMessages(m => m.map(msg =>
        msg.id === msgId
          ? { ...msg, meta: { ...msg.meta, applied: true, applied_result: result } }
          : msg,
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply fix');
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <>
      {/* Floating trigger — sticks to bottom-right on every authenticated page. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="ask-ai-button"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg bg-primary text-primary-foreground hover:brightness-110 active:scale-95 transition-all"
        aria-label="Open AI Data Assistant"
      >
        <Sparkles size={18} />
        <span className="text-sm font-semibold">Ask AI</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg p-0 flex flex-col"
          data-testid="ask-ai-panel"
        >
          <SheetHeader className="px-5 py-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2 text-wv-ink">
              <Sparkles size={18} className="text-primary" />
              AI Data Assistant
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Ask about any data-flow issue in RADAR — I know your sheets, mappings, and past incidents. Session-only chat.
            </SheetDescription>
          </SheetHeader>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-muted/20"
            data-testid="ask-ai-messages"
          >
            {messages.length === 0 && (
              <EmptyState onPick={sendMessage} />
            )}

            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                msg={m}
                canApplyFixes={canApplyFixes}
                isApplying={applyingId === m.id}
                onApply={applyFix}
              />
            ))}

            {sending && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ask-ai-thinking">
                <span className="inline-block h-2 w-2 rounded-full bg-primary animate-pulse" />
                Diagnosing…
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="border-t border-border px-4 py-3 flex items-end gap-2 bg-background"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
              }}
              placeholder="Describe the issue… (Enter to send, Shift+Enter for newline)"
              disabled={sending}
              rows={2}
              data-testid="ask-ai-input"
              className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
            />
            <Button
              type="submit"
              size="icon"
              disabled={sending || !input.trim()}
              data-testid="ask-ai-send"
              className="shrink-0"
            >
              <Send size={16} />
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="space-y-3" data-testid="ask-ai-empty-state">
      <p className="text-sm text-wv-body">Try one of these:</p>
      <div className="flex flex-col gap-2">
        {SUGGESTION_CHIPS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onPick(c)}
            data-testid={`ask-ai-suggestion-${c.slice(0, 20).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            className="text-left text-sm px-3 py-2 rounded-md border border-border bg-card hover:bg-muted hover:border-primary/40 transition-colors"
          >
            {c}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
        I can diagnose broken column mappings, missing sheet tabs, or mismatched
        headers — and safely apply low-risk config fixes on your behalf. Source-data
        changes I&apos;ll explain step-by-step so ops can make them in Google Sheets.
      </p>
    </div>
  );
}

function MessageBubble({ msg, canApplyFixes, isApplying, onApply }) {
  if (msg.role === 'user') return <UserBubble msg={msg} />;
  return (
    <AssistantBubble
      msg={msg}
      canApplyFixes={canApplyFixes}
      isApplying={isApplying}
      onApply={onApply}
    />
  );
}

function UserBubble({ msg }) {
  return (
    <div className="flex justify-end" data-testid={`ask-ai-msg-user-${msg.id}`}>
      <div className="max-w-[85%] rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap">
        {msg.content}
      </div>
    </div>
  );
}

function AssistantBubble({ msg, canApplyFixes, isApplying, onApply }) {
  const meta = msg.meta || {};
  return (
    <div className="flex justify-start" data-testid={`ask-ai-msg-assistant-${msg.id}`}>
      <div className="max-w-[90%] w-full rounded-lg bg-card border border-border shadow-sm">
        <MetaChips module={meta.module} sheet={meta.affected_sheet} />
        <div className="px-3 py-2 text-sm text-wv-ink prose prose-sm max-w-none prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-code:text-xs">
          <ReactMarkdown>{msg.content}</ReactMarkdown>
        </div>
        <RootCauseBlock text={meta.root_cause} />
        <SuggestionCard
          suggestion={meta.suggestion}
          applied={meta.applied}
          canApplyFixes={canApplyFixes}
          isApplying={isApplying}
          onApply={() => onApply(msg.id, meta.suggestion)}
          testId={`ask-ai-apply-fix-${msg.id}`}
        />
      </div>
    </div>
  );
}

function MetaChips({ module: mod, sheet }) {
  if (!mod && !sheet) return null;
  return (
    <div className="px-3 pt-3 flex flex-wrap items-center gap-1.5">
      {mod && (
        <Badge variant="secondary" className="text-[10px] font-medium">
          <Info size={10} className="mr-1" />
          {mod}
        </Badge>
      )}
      {sheet && (
        <Badge variant="outline" className="text-[10px] font-medium">{sheet}</Badge>
      )}
    </div>
  );
}

function RootCauseBlock({ text }) {
  if (!text) return null;
  return (
    <div className="px-3 pb-2">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Root cause</p>
      <p className="text-xs text-wv-body">{text}</p>
    </div>
  );
}

function SuggestionCard({ suggestion, applied, canApplyFixes, isApplying, onApply, testId }) {
  if (!suggestion) return null;
  const isSafeFix = suggestion.action !== 'explain_only' && suggestion.safe_to_apply;
  return (
    <div className="border-t border-border px-3 py-3 bg-muted/30 rounded-b-lg space-y-2">
      <div className="flex items-start gap-2">
        {isSafeFix
          ? <Wrench size={14} className="text-primary mt-0.5 shrink-0" />
          : <ShieldAlert size={14} className="text-amber-600 mt-0.5 shrink-0" />}
        <div className="flex-1">
          <p className="text-xs font-semibold text-wv-ink">{suggestion.title}</p>
          {suggestion.action === 'explain_only' && suggestion.manual_steps && (
            <div className="mt-1.5 text-xs text-wv-body whitespace-pre-wrap leading-relaxed">
              {suggestion.manual_steps}
            </div>
          )}
        </div>
      </div>
      {isSafeFix && !applied && canApplyFixes && (
        <Button
          size="sm"
          onClick={onApply}
          disabled={isApplying}
          data-testid={testId}
          className="w-full"
        >
          {isApplying ? 'Applying…' : 'Apply Fix'}
        </Button>
      )}
      {isSafeFix && !applied && !canApplyFixes && (
        <p className="text-[11px] text-muted-foreground italic">
          Apply Fix is restricted — ask an editor to run this change.
        </p>
      )}
      {applied && (
        <div className="flex items-center gap-1.5 text-xs text-green-700 font-medium">
          <CheckCircle2 size={14} />
          Applied — the app will pick it up on the next refresh.
        </div>
      )}
    </div>
  );
}
